import { useState } from "react";
import { vehicleImage } from "../data/content";
import { useSiteContent } from "../siteContent";
import { websiteService } from "../../services/websiteService";
import { ChoiceButton, Field, Notice, fieldClass } from "../components/fields";

export default function Contact() {
  const { company, contactTopics, desks } = useSiteContent();
  const [topic, setTopic] = useState("");
  const activeTopic = topic || contactTopics[0] || "General";
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSending(true);
    try {
      const response = await websiteService.submitMessage({ ...form, topic: activeTopic });
      setSubmitted(response.data?.data || { reference: "" });
    } catch (err) {
      setError(err.response?.data?.error || "We could not send that message. Please call the helpline.");
    } finally {
      setSending(false);
    }
  };

  const mapsQuery = encodeURIComponent(company.mapQuery || "Essaar Transport Logistics Coimbatore");

  return (
    <>
      <section className="bg-[#0B6A34] text-white">
        <div className="container-page py-14 sm:py-16">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b7e7a8]">Contact us</p>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-5xl">Talk to a person at Essaar</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
            Call the cargo helpline, write to us, or send a message below. For a specific branch, use the branch locator.
          </p>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="container-page grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-3">
            <article className="rounded-xl border border-[#d5e4d9] p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-[#5d6b62]">Cargo helpline</p>
              <a href={company.phoneHref} className="mt-1 block text-2xl font-bold text-[#0B6A34]">{company.phone}</a>
              <p className="mt-1 text-sm text-slate-500">{company.helplineHours}</p>
            </article>
            <article className="rounded-xl border border-[#d5e4d9] p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-[#5d6b62]">Email and web</p>
              <a href={company.emailHref} className="mt-2 block font-semibold text-[#16301f]">{company.email}</a>
              <a href={company.websiteHref} target="_blank" rel="noreferrer" className="mt-1 block text-sm text-[#0B6A34]">{company.website}</a>
            </article>
            <article className="rounded-xl border border-[#d5e4d9] p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-[#5d6b62]">Head office</p>
              <p className="mt-2 font-semibold text-[#16301f]">{company.fullName}</p>
              <p className="text-sm text-slate-600">{company.headOffice}</p>
              <p className="mt-1 text-sm text-slate-500">{company.headOfficeNote}</p>
            </article>
            <article className="rounded-xl bg-[#f4f7f5] p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-[#5d6b62]">Looking for bus tickets?</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Bus booking is on a separate site: {company.busWebsite}, or call {company.busPhone}.
              </p>
              <a href={company.busWebsiteHref} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-semibold text-[#0B6A34] hover:underline">
                Go to essaar.net
              </a>
            </article>
          </div>

          {submitted ? (
            <Notice>
              <p className="font-semibold">Thanks, {form.name}. Your message is with Essaar.</p>
              <p className="mt-2">
                Reference {submitted.reference}. A colleague will reply about {activeTopic.toLowerCase()}. You can also call {company.phone}.
              </p>
            </Notice>
          ) : (
            <form onSubmit={handleSubmit} className="rounded-xl border border-[#d5e4d9] p-5 sm:p-6">
              <h2 className="text-xl font-bold text-[#16301f]">Send us a message</h2>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#5d6b62]">What is it about?</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {contactTopics.map((item) => (
                  <ChoiceButton key={item} selected={activeTopic === item} onClick={() => setTopic(item)}>
                    {item}
                  </ChoiceButton>
                ))}
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Your name">
                  <input name="name" required value={form.name} onChange={update} placeholder="Full name" className={fieldClass} />
                </Field>
                <Field label="Mobile number">
                  <input name="mobile" required inputMode="tel" value={form.mobile} onChange={update} placeholder="10-digit mobile" className={fieldClass} />
                </Field>
                <Field label="Email (optional)" className="sm:col-span-2">
                  <input name="email" type="email" value={form.email} onChange={update} placeholder="name@company.com" className={fieldClass} />
                </Field>
                <Field label="Your message" className="sm:col-span-2">
                  <textarea name="message" required rows={5} value={form.message} onChange={update} placeholder="How can we help?" className={fieldClass} />
                </Field>
              </div>
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={sending} className="mt-5 rounded-md bg-[#0B6A34] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#085628] disabled:opacity-60">
                {sending ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="bg-[#f4f7f5] py-14">
        <div className="container-page">
          <h2 className="text-2xl font-bold text-[#16301f]">Who to contact for what</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {desks.map((desk) => (
              <article key={desk.title} className="rounded-xl border border-[#d5e4d9] bg-white p-4">
                <h3 className="font-bold text-[#16301f]">{desk.title}</h3>
                <p className="mt-2 text-sm text-slate-500">{desk.detail}</p>
              </article>
            ))}
          </div>
          <div className="mt-6 overflow-hidden rounded-xl border border-dashed border-[#b7c9bc] bg-white">
            <img src={vehicleImage} alt="Essaar head office area" className="h-52 w-full object-cover" />
            <div className="flex flex-col items-start justify-between gap-3 p-4 sm:flex-row sm:items-center">
              <p className="text-sm text-slate-600">Head office location in Coimbatore. Street address will be added here.</p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold text-[#0B6A34] hover:underline"
              >
                Get directions
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
